import { ref, computed, h } from 'vue'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  useVueTable
} from '@tanstack/vue-table'

const columnHelper = createColumnHelper()

function includesFilter(row, id, filter) {
  if (!filter) return true
  const value = String(row.getValue(id) ?? '').toLowerCase()
  return value.includes(String(filter).toLowerCase())
}

function textColumn(accessor, header) {
  return columnHelper.accessor(accessor, {
    header,
    cell: info => info.getValue() || '-',
    filterFn: includesFilter
  })
}

export function useStudentsTable(studentsGetter) {
  const sorting = ref([])
  const pagination = ref({
    pageIndex: 0,
    pageSize: 25
  })
  const columnFilters = ref([])
  const expanded = ref({})
  const columnVisibility = ref({})

  const columns = [
    // Expand toggle column
    columnHelper.display({
      id: 'expand',
      header: '',
      enableHiding: false,
      cell: ({ row }) => {
        return h(
          'button',
          {
            onClick: row.getToggleExpandedHandler(),
            class: 'btn btn-sm btn-outline-primary border-0',
            style: { cursor: 'pointer' }
          },
          row.getIsExpanded() 
            ? h('i', { class: 'fas fa-chevron-down' })
            : h('i', { class: 'fas fa-chevron-right' })
        )
      }
    }),
    textColumn('studentId', 'Matrikelnummer'),
    textColumn('lastname', 'Nachname'),
    textColumn('firstname', 'Vorname'),
    textColumn('program', 'Studiengang'),
    textColumn('standardPeriodOfStudy', 'RSZ'),
    textColumn('currentSemesterCount', 'FS'),
    textColumn('teachingClaim', 'UA HF'),
    textColumn('majorSubject', 'HF'),
    columnHelper.accessor('budget.total', {
      header: 'Budget (Punkte)',
      cell: info => {
        const total = info.getValue()
        return total !== undefined ? total : '-'
      }
    }),
    columnHelper.accessor('budget.used', {
      header: 'Verbraucht',
      cell: info => {
        const used = info.getValue()
        return used !== undefined ? used : '-'
      }
    }),
    columnHelper.accessor('budget.available', {
      header: 'Verfügbar',
      cell: info => {
        const available = info.getValue()
        return available !== undefined ? available : '-'
      }
    })
  ]

  const data = computed(() => {
    const students = typeof studentsGetter === 'function' ? studentsGetter() : studentsGetter
    return students || []
  })

  const table = useVueTable({
    get data() { return data.value },
    columns,
    state: {
      get sorting() { return sorting.value },
      get pagination() { return pagination.value },
      get columnFilters() { return columnFilters.value },
      get expanded() { return expanded.value },
      get columnVisibility() { return columnVisibility.value }
    },
    onSortingChange: updaterOrValue => {
      sorting.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(sorting.value) 
        : updaterOrValue
    },
    onPaginationChange: updaterOrValue => {
      pagination.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(pagination.value) 
        : updaterOrValue
    },
    onColumnFiltersChange: updaterOrValue => {
      columnFilters.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(columnFilters.value) 
        : updaterOrValue
    },
    onExpandedChange: updaterOrValue => {
      expanded.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(expanded.value) 
        : updaterOrValue
    },
    onColumnVisibilityChange: updaterOrValue => {
      columnVisibility.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(columnVisibility.value) 
        : updaterOrValue
    },
    getRowId: row => row.id, // Use student ID as stable row ID
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Enable client-side filtering using TanStack's getFilteredRowModel
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false, // Keep expanded rows when data changes
    // Client-side filtering; see Task 5 for details
  })

  return {
    table,
    sorting,
    pagination,
    columnFilters,
    expanded,
    columnVisibility
  }
}
